import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SearchIcon, UserIcon, AlertCircleIcon, SendIcon, MicIcon, StopCircleIcon } from 'lucide-react';
import { useMessages } from './MessageContext';
import { useAuth } from '../auth/AuthContext';
// Mock data for contacts
const MOCK_CONTACTS = [{
  id: 'foreman-1',
  name: 'John Smith',
  role: 'foreman'
}, {
  id: 'foreman-2',
  name: 'Mike Johnson',
  role: 'foreman'
}, {
  id: 'broker-1',
  name: 'Sarah Williams',
  role: 'broker'
}, {
  id: 'broker-2',
  name: 'David Chen',
  role: 'broker'
}, {
  id: 'trucking-1',
  name: 'Mountain Valley Trucking',
  role: 'trucking'
}, {
  id: 'trucking-2',
  name: 'Alpine Hauling',
  role: 'trucking'
}, {
  id: 'driver-1',
  name: 'Mike Rodriguez',
  role: 'driver'
}, {
  id: 'driver-2',
  name: 'Tom Williams',
  role: 'driver'
}, {
  id: 'contractor-1',
  name: 'ABC Construction',
  role: 'contractor'
}, {
  id: 'broker-3',
  name: 'XYZ Brokerage',
  role: 'broker'
}];
// Mock job data
const MOCK_JOBS = [{
  id: 'job-1',
  name: 'Downtown Commercial Complex Phase 1'
}, {
  id: 'job-2',
  name: 'Highway 89 Expansion Project'
}, {
  id: 'job-3',
  name: 'Residential Development Phase 2'
}, {
  id: 'job-4',
  name: 'Mountain View Condos Site Prep'
}];
interface NewMessageModalProps {
  onClose: () => void;
  initialJobId?: string;
}
export const NewMessageModal: React.FC<NewMessageModalProps> = ({
  onClose,
  initialJobId
}) => {
  const {
    user
  } = useAuth();
  const {
    sendMessage,
    setActiveConversationId,
    startNewConversation,
    getJobContacts
  } = useMessages();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState<{
    id: string;
    name: string;
    role: string;
  } | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(initialJobId);
  const [messageText, setMessageText] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [step, setStep] = useState<'select-contact' | 'compose-message'>('select-contact');
  const [jobContacts, setJobContacts] = useState<Array<{
    id: string;
    name: string;
    role: string;
  }>>([]);
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Speech recognition setup
  const recognition = useRef<SpeechRecognition | null>(null);
  // Initialize speech recognition
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.onresult = event => {
        const transcript = Array.from(event.results).map(result => result[0].transcript).join('');
        setMessageText(transcript);
        // Focus and move cursor to end of textarea
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      };
      recognition.current.onerror = event => {
        console.error('Speech recognition error', event.error);
        setRecordingError('Error with speech recognition. Please try again.');
        setIsRecording(false);
      };
      recognition.current.onend = () => {
        setIsRecording(false);
      };
    }
    return () => {
      if (recognition.current) {
        recognition.current.onresult = null;
        recognition.current.onend = null;
        recognition.current.onerror = null;
        if (isRecording) {
          recognition.current.stop();
        }
      }
    };
  }, []);
  const toggleRecording = () => {
    if (!recognition.current) {
      setRecordingError('Speech recognition is not supported in your browser.');
      return;
    }
    setRecordingError('');
    if (isRecording) {
      recognition.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognition.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setRecordingError('Could not start recording. Please try again.');
      }
    }
  };
  // Filter contacts based on search query and user role
  const filteredContacts = MOCK_CONTACTS.filter(contact => contact.id !== user?.id && contact.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Get job contacts when initialJobId changes
  useEffect(() => {
    if (initialJobId) {
      const contacts = getJobContacts(initialJobId);
      setJobContacts(contacts.map(c => ({
        id: c.participantId,
        name: c.participantName,
        role: c.participantRole
      })));
    }
  }, [initialJobId, getJobContacts]);
  const handleSelectContact = (contact: {
    id: string;
    name: string;
    role: string;
  }) => {
    setSelectedContact(contact);
    setStep('compose-message');
  };
  const handleSendMessage = () => {
    if (!selectedContact || !messageText.trim()) return;
    // Create a new conversation or get existing one
    const conversationId = startNewConversation(selectedContact.id, selectedContact.name, selectedContact.role, selectedJobId, selectedJobId ? MOCK_JOBS.find(j => j.id === selectedJobId)?.name : undefined);
    // Send the message
    sendMessage(selectedContact.id, messageText, selectedJobId, isUrgent);
    // Set the active conversation and close modal
    setActiveConversationId(conversationId);
    onClose();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-389">
      <div className="bg-white rounded-lg w-full max-w-md shadow-xl" data-id="element-390">
        <div className="p-4 border-b flex justify-between items-center" data-id="element-391">
          <h3 className="font-bold text-lg" data-id="element-392">
            {step === 'select-contact' ? 'New Message' : 'Compose Message'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-393">
            <XIcon size={20} data-id="element-394" />
          </button>
        </div>
        {step === 'select-contact' ? <div data-id="element-395">
            {/* Job selection if not provided */}
            {!initialJobId && <div className="p-4 border-b" data-id="element-396">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-397">
                  Select Job (Optional)
                </label>
                <select className="w-full border-gray-300 rounded-md shadow-sm p-2 border" value={selectedJobId || ''} onChange={e => {
            const jobId = e.target.value || undefined;
            setSelectedJobId(jobId);
            if (jobId) {
              const contacts = getJobContacts(jobId);
              setJobContacts(contacts.map(c => ({
                id: c.participantId,
                name: c.participantName,
                role: c.participantRole
              })));
            } else {
              setJobContacts([]);
            }
          }} data-id="element-398">
                  <option value="" data-id="element-399">-- No job selected --</option>
                  {MOCK_JOBS.map(job => <option key={job.id} value={job.id} data-id="element-400">
                      {job.name}
                    </option>)}
                </select>
              </div>}
            {/* Search */}
            <div className="p-4 border-b" data-id="element-401">
              <div className="relative" data-id="element-402">
                <input type="text" placeholder="Search contacts..." className="w-full pl-9 pr-4 py-2 border rounded-md" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-403" />
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-404" />
              </div>
            </div>
            {/* Job Contacts Section */}
            {selectedJobId && jobContacts.length > 0 && <div className="p-4 border-b" data-id="element-405">
                <h4 className="font-medium text-sm text-gray-700 mb-2" data-id="element-406">
                  Job Contacts
                </h4>
                <div className="space-y-2" data-id="element-407">
                  {jobContacts.map(contact => <div key={contact.id} className="p-2 border rounded-md cursor-pointer hover:bg-gray-50 flex items-center" onClick={() => handleSelectContact(contact)} data-id="element-408">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-409">
                        <UserIcon size={16} data-id="element-410" />
                      </div>
                      <div data-id="element-411">
                        <div className="font-medium" data-id="element-412">{contact.name}</div>
                        <div className="text-xs text-gray-500 capitalize" data-id="element-413">
                          {contact.role}
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
            {/* All Contacts */}
            <div className="max-h-[300px] overflow-y-auto p-4" data-id="element-414">
              <h4 className="font-medium text-sm text-gray-700 mb-2" data-id="element-415">
                All Contacts
              </h4>
              {filteredContacts.length === 0 ? <div className="text-center py-4 text-gray-500" data-id="element-416">
                  No contacts found matching your search
                </div> : <div className="space-y-2" data-id="element-417">
                  {filteredContacts.map(contact => <div key={contact.id} className="p-2 border rounded-md cursor-pointer hover:bg-gray-50 flex items-center" onClick={() => handleSelectContact(contact)} data-id="element-418">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-419">
                        <UserIcon size={16} data-id="element-420" />
                      </div>
                      <div data-id="element-421">
                        <div className="font-medium" data-id="element-422">{contact.name}</div>
                        <div className="text-xs text-gray-500 capitalize" data-id="element-423">
                          {contact.role}
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>
          </div> : <div data-id="element-424">
            {/* Selected Contact */}
            <div className="p-4 border-b" data-id="element-425">
              <div className="flex items-center" data-id="element-426">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3" data-id="element-427">
                  <UserIcon size={18} data-id="element-428" />
                </div>
                <div data-id="element-429">
                  <div className="font-medium" data-id="element-430">{selectedContact?.name}</div>
                  <div className="text-sm text-gray-500 capitalize" data-id="element-431">
                    {selectedContact?.role}
                    {selectedJobId && <>
                        <span className="mx-1" data-id="element-432">•</span>
                        {MOCK_JOBS.find(j => j.id === selectedJobId)?.name}
                      </>}
                  </div>
                </div>
              </div>
            </div>
            {/* Message Compose */}
            <div className="p-4" data-id="element-433">
              <div className="mb-4" data-id="element-434">
                <div className="flex items-center justify-between mb-2" data-id="element-435">
                  <label className="flex items-center text-sm cursor-pointer" data-id="element-436">
                    <input type="checkbox" checked={isUrgent} onChange={() => setIsUrgent(!isUrgent)} className="mr-2 h-4 w-4 text-red-500" data-id="element-437" />
                    <AlertCircleIcon size={14} className="text-red-500 mr-1" data-id="element-438" />
                    Mark as urgent
                  </label>
                  <button type="button" onClick={toggleRecording} className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${isRecording ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-439">
                    {isRecording ? <StopCircleIcon size={14} data-id="element-440" /> : <MicIcon size={14} data-id="element-441" />}
                    {isRecording ? 'Stop' : 'Voice Input'}
                  </button>
                </div>
                {recordingError && <div className="text-red-500 text-xs mb-2" data-id="element-442">
                    {recordingError}
                  </div>}
                <div className="relative" data-id="element-443">
                  <textarea ref={textareaRef} value={messageText} onChange={e => setMessageText(e.target.value)} placeholder="Type your message..." className={`w-full border rounded-md p-3 resize-none ${isRecording ? 'border-red-400 bg-red-50' : ''}`} rows={5} data-id="element-444" />
                  {isRecording && <div className="absolute bottom-2 right-2 flex items-center gap-2 text-red-500 text-xs animate-pulse" data-id="element-445">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full" data-id="element-446"></span>
                      Recording...
                    </div>}
                </div>
              </div>
            </div>
          </div>}
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between" data-id="element-447">
          {step === 'select-contact' ? <div data-id="element-448"></div> : <button onClick={() => setStep('select-contact')} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-449">
              Back
            </button>}
          {step === 'select-contact' ? <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100" data-id="element-450">
              Cancel
            </button> : <button onClick={handleSendMessage} disabled={!messageText.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2" data-id="element-451">
              <SendIcon size={16} data-id="element-452" />
              Send
            </button>}
        </div>
      </div>
    </div>;
};